import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { dbBreakglassService } from '../services/dbBreakglassService';
import { sanitizeTerminalId } from '../utils/terminalConfig';

export const MIN_DURATION_HOURS = 1;
export const MAX_DURATION_HOURS = 6;
const DEFAULT_DURATION_HOURS = '1';

const describeError = (error: any): string => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || error?.response?.data?.message;
    switch (status) {
        case 400: return `Invalid request${detail ? `: ${detail}` : ''}. Check the terminal, incident number and duration.`;
        case 401: return 'Your session has expired. Please sign in again.';
        case 403: return 'You are not authorised to raise a DB breakglass request.';
        case 502: return 'The DB breakglass service failed. Please contact support before trying again.';
        default:
            if (!status) return 'No response from the server. The request may have been processed; verify before trying again.';
            return detail || 'Failed to raise DB breakglass request.';
    }
};

export const INCIDENT_NUMBER_PATTERN = /^[A-Za-z]{3}[0-9]{7}$/;

export const useDbBreakglassRequestController = () => {
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const userEmail = user?.email ?? '';

    const [terminalCode, setTerminalCode] = useState('');
    const [incidentNumber, setIncidentNumber] = useState('');
    const [duration, setDuration] = useState(DEFAULT_DURATION_HOURS);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const inFlight = useRef(false);

    const durationHours = Number(duration);
    const isDurationValid = duration.trim() !== ''
        && Number.isInteger(durationHours)
        && durationHours >= MIN_DURATION_HOURS
        && durationHours <= MAX_DURATION_HOURS;

    const isIncidentValid = INCIDENT_NUMBER_PATTERN.test(incidentNumber);

    const canSubmit = Boolean(userEmail && terminalCode && isIncidentValid && isDurationValid);

    const resetForm = () => {
        setTerminalCode('');
        setIncidentNumber('');
        setDuration(DEFAULT_DURATION_HOURS);
    };

    const submitMutation = useMutation({
        mutationFn: dbBreakglassService.createRequest,
        retry: false,
        onSettled: () => { inFlight.current = false; },
        onSuccess: (response) => {
            showNotification(response.message || `DB breakglass request ${response.requestId} raised.`, 'success');
            setConfirmOpen(false);
            resetForm();
        },
        onError: (error: any, variables) => {
            console.error('[DbBreakglass] request failed', {
                status: error?.response?.status,
                terminalCode: variables.terminalCode,
                incidentNumber: variables.incidentNumber,
                correlationId: error?.config?.headers?.['X-Correlation-Id'],
                message: error?.message,
            });
            showNotification(describeError(error), 'error');
            setConfirmOpen(false);
        },
    });

    const submit = () => {
        if (!canSubmit || inFlight.current) return;
        inFlight.current = true;
        submitMutation.mutate({
            userId: userEmail,
            terminalCode: sanitizeTerminalId(terminalCode),
            incidentNumber,
            duration: durationHours,
        });
    };

    return {
        terminalCode, setTerminalCode,
        incidentNumber, setIncidentNumber, isIncidentValid,
        duration, setDuration,
        confirmOpen, setConfirmOpen,
        canSubmit,
        isSubmitting: submitMutation.isPending,
        submit,
    };
};
